import type { Health, Progress } from '@/types';
import { ProgressBars } from './ProgressBars';
import styles from './Card.module.css';

interface CardProps {
  bannerColor: string;
  icon?: React.ReactNode;
  title: string;
  subtitle?: string | null;
  stats?: { num: string | number; label: string }[];
  progress?: { done: number; total: number; pct: number; label?: string; color?: string };
  /** R4: dos barras (hitos + compromisos) heredadas en cualquier nivel de la jerarquía. */
  progressBars?: Progress;
  /** R6: estado de salud (rojo/amarillo/verde) heredado del subárbol — controla el glow. */
  health?: Health;
  footer?: React.ReactNode;
  /** R5: fila adicional de acciones al final de la card (debajo del footer). */
  actions?: React.ReactNode;
  onClick?: () => void;
}

const HEALTH_CLASS: Record<Health, string> = {
  red: 'healthRed',
  yellow: 'healthYellow',
  green: 'healthGreen',
  neutral: '',
};

export function Card({
  bannerColor,
  icon,
  title,
  subtitle,
  stats,
  progress,
  progressBars,
  health,
  footer,
  actions,
  onClick,
}: CardProps) {
  const fillColor =
    progress?.color ||
    (progress && progress.pct >= 80
      ? 'var(--st-done)'
      : progress && progress.pct >= 30
        ? 'var(--brand)'
        : 'var(--st-pend)');

  const healthClass = health ? styles[HEALTH_CLASS[health]] || '' : '';

  return (
    <div
      className={`${styles.tile} ${healthClass}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className={styles.banner} style={{ background: bannerColor }} />
      <div className={styles.body}>
        <div className={styles.head}>
          <div>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          {icon && (
            <div className={styles.icon} style={{ background: bannerColor }}>
              {icon}
            </div>
          )}
        </div>

        {stats && stats.length > 0 && (
          <div className={styles.stats}>
            {stats.map((s, i) => (
              <div key={i} className={styles.stat}>
                <div className={styles.statNum}>{s.num}</div>
                <div className={styles.statLbl}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {progress && (
          <div className={styles.progress}>
            <div className={styles.progressRow}>
              <span>{progress.label || 'Avance'}</span>
              <span>
                {progress.done} / {progress.total} · {progress.pct}%
              </span>
            </div>
            <div className={styles.bar}>
              <div
                className={styles.fill}
                style={{ width: `${progress.pct}%`, background: fillColor }}
              />
            </div>
          </div>
        )}

        {progressBars && <ProgressBars progress={progressBars} color={bannerColor} />}

        {footer && <div className={styles.footer}>{footer}</div>}

        {actions && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 10,
              paddingTop: 10,
              borderTop: '1px dashed var(--border)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
